/**
 * Polling rules shared by the web app and the native app. Every poll here costs
 * a fresh edge worker (about 1.5 s), so a poll backs off while nothing changes,
 * never overlaps itself, and stops while its screen is not in front. Plain
 * TypeScript with no DOM or React Native imports, so both apps load this file.
 */

export type PollOutcome = 'changed' | 'unchanged' | 'failed';

export interface BackoffPolicy {
  baseMs: number;
  maxMs: number;
  /** Growth per quiet or failed run. Defaults to 2. */
  factor?: number;
}

/** A change snaps back to the base interval; a quiet or failed run stretches it up to the cap. */
export function nextPollDelay(previousMs: number | null, outcome: PollOutcome, policy: BackoffPolicy): number {
  if (outcome === 'changed' || previousMs === null) return policy.baseMs;
  const grown = previousMs * (policy.factor ?? 2);
  return Math.min(policy.maxMs, Math.max(policy.baseMs, grown));
}

/** Callers asking for the same key while a request runs share that request instead of starting another. */
export function createSingleFlight<K, T>() {
  const running = new Map<K, Promise<T>>();
  return {
    run(key: K, task: () => Promise<T>): Promise<T> {
      const existing = running.get(key);
      if (existing) return existing;
      const promise = (async () => task())().finally(() => {
        if (running.get(key) === promise) running.delete(key);
      });
      running.set(key, promise);
      return promise;
    },
    isRunning(key: K): boolean {
      return running.has(key);
    },
  };
}

/**
 * What one poll reports: true when the data changed, false when it did not,
 * or 'skipped' when it bailed out without reading (another load running, the
 * app going to the background, a request aborted). A skipped run leaves the
 * interval and the last-run time alone, so a return straight after it still
 * reads. A rejection counts as a failure and backs off.
 */
export type PollResult = boolean | 'skipped';

export interface AdaptivePollerOptions {
  run: () => Promise<PollResult>;
  policy: BackoffPolicy;
  /**
   * resume() inside this long of the last completed run waits for the next due
   * time instead of running. A function is read on each resume, for a gap that
   * follows other state (push live or not) without rebuilding the poller.
   */
  minGapMs?: number | (() => number);
  now?: () => number;
}

export interface AdaptivePoller {
  /** The screen is in front: keep polling, and run now if the last run is older than minGapMs. */
  resume(): void;
  /** The screen went away: no timer fires until resume(). A run already under way finishes. */
  pause(): void;
  /**
   * Something says the data moved (a push, a realtime event, pull to refresh):
   * run now from the base interval. Resolves once that read, including a rerun
   * queued behind one already under way, has finished.
   */
  trigger(): Promise<void>;
  /**
   * A read happened outside the poller (a bootstrap load, a realtime-driven
   * reload): count it as this poll's run so the next one waits a full interval.
   * Ignored while a run is under way, which records itself.
   */
  record(outcome?: PollOutcome): void;
  setPolicy(policy: BackoffPolicy): void;
  stop(): void;
  readonly delayMs: number;
  readonly lastRunAt: number | null;
}

export function createAdaptivePoller(options: AdaptivePollerOptions): AdaptivePoller {
  const now = options.now ?? (() => Date.now());
  const minGap = () => (typeof options.minGapMs === 'function' ? options.minGapMs() : options.minGapMs ?? 0);
  let policy = options.policy;
  let active = false;
  let stopped = false;
  let delay: number | null = null;
  let lastRunAt: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> | null = null;
  // A trigger during a run means the data may have moved after that request
  // left, so its answer cannot be trusted as current: run once more after it.
  let rerun = false;
  // A skipped run tries again a base interval later, never in a tight loop.
  let skippedAt: number | null = null;

  const clearTimer = () => {
    if (timer !== null) { clearTimeout(timer); timer = null; }
  };

  const schedule = () => {
    clearTimer();
    if (!active || stopped || inFlight) return;
    const wait = delay ?? policy.baseMs;
    let dueIn = lastRunAt === null ? wait : Math.max(0, lastRunAt + wait - now());
    if (skippedAt !== null) dueIn = Math.max(dueIn, skippedAt + policy.baseMs - now());
    timer = setTimeout(() => { timer = null; void runNow(); }, dueIn);
  };

  const runNow = (): Promise<void> => {
    if (stopped) return Promise.resolve();
    if (inFlight) return inFlight;
    clearTimer();
    const flight = (async () => {
      let outcome: PollOutcome;
      try {
        const result = await options.run();
        if (result === 'skipped') { skippedAt = now(); return; }
        outcome = result ? 'changed' : 'unchanged';
      } catch {
        outcome = 'failed';
      }
      skippedAt = null;
      lastRunAt = now();
      delay = nextPollDelay(delay, outcome, policy);
    })();
    inFlight = flight.finally(() => {
      inFlight = null;
      if (rerun && !stopped) {
        rerun = false;
        delay = null;
        void runNow();
        return;
      }
      schedule();
    });
    return inFlight;
  };

  return {
    resume() {
      if (stopped) return;
      active = true;
      if (inFlight) return;
      if (lastRunAt === null || now() - lastRunAt >= minGap()) {
        // Coming back to a screen is attention: start again from the base interval.
        delay = null;
        void runNow();
      } else {
        schedule();
      }
    },
    pause() {
      active = false;
      clearTimer();
    },
    trigger() {
      if (stopped) return Promise.resolve();
      if (inFlight) {
        rerun = true;
        // By the time the current run settles, inFlight is the queued rerun.
        return inFlight.then(() => inFlight ?? undefined);
      }
      delay = null;
      return runNow();
    },
    record(outcome = 'unchanged') {
      if (stopped || inFlight) return;
      skippedAt = null;
      lastRunAt = now();
      delay = nextPollDelay(delay, outcome, policy);
      schedule();
    },
    setPolicy(next) {
      const same = next.baseMs === policy.baseMs && next.maxMs === policy.maxMs && next.factor === policy.factor;
      policy = next;
      if (same) return;
      // A new regime (realtime dropped, push confirmed) starts from its own base.
      delay = null;
      if (timer !== null) schedule();
    },
    stop() {
      stopped = true;
      active = false;
      rerun = false;
      clearTimer();
    },
    get delayMs() {
      return delay ?? policy.baseMs;
    },
    get lastRunAt() {
      return lastRunAt;
    },
  };
}
